<script setup lang="ts">
import type { InverterSubmission } from '../types/inverterSubmission'
import { SolAlert } from '@solfacil/girassol'
import CheckCircleIcon from '~icons/material-symbols/check-circle-outline-rounded'
import EditIcon from '~icons/material-symbols/edit-outline'
import Tooltip from '~/components/tooltip/index.vue'
import useCommissioningRequest, { CommissioningRequestError } from '~/store-v2/installation/useCommissioningRequest'
import { getErrorMessage } from '~/utils/errors'
import { holdUntilStatusChanges } from '../composables/useCommissioningOptimisticHold'
import useInstallationAlerts, { resolveInstallationErrorStatus } from '../composables/useInstallationAlerts'
import { useInstallationPendingEquipment } from '../composables/useInstallationPendingEquipment'
import { getPortalNameByBrand } from '../schemas/portals'
import useInstallationFlow, { steps } from '../useInstallationFlow'
import InversorCard from './InversorCard.vue'

interface Props {
  submission: InverterSubmission
  projectId: string
  installationId?: string
}

const props = withDefaults(defineProps<Props>(), {
  installationId: '',
})

const emit = defineEmits<{
  edit: []
}>()

const { t } = useI18n()

const {
  hideInstallationAlert,
  getErrorAlertConfig,
  showInstallationAlert,
} = useInstallationAlerts()
const { handleStepChange } = useInstallationFlow(computed(() => props.projectId))
const { hasPendingTAF } = useInstallationPendingEquipment(computed(() => props.projectId))

const installationIdRef = computed(() => props.installationId || undefined)
const projectIdRef = computed(() => props.projectId)

const confirmationError = ref<{ title: string, text: string | string[] } | null>(null)

const {
  mutateAsync: commissioningRequest,
  isPending: isCommissioningRequestPending,
  reset: resetCommissioningRequest,
} = useCommissioningRequest(installationIdRef, projectIdRef)

const portalName = computed(() => getPortalNameByBrand(props.submission.brand))

const isLoading = computed(() => isCommissioningRequestPending.value)

const isButtonDisabled = computed(() => isLoading.value || hasPendingTAF.value)

function handleEdit(): void {
  confirmationError.value = null
  hideInstallationAlert()
  emit('edit')
}

async function handleConfirm(): Promise<void> {
  if (!props.installationId) {
    confirmationError.value = {
      title: 'Não foi possível confirmar',
      text: 'Identificador da instalação indisponível. Recarregue a página e tente novamente.',
    }
    return
  }

  confirmationError.value = null
  hideInstallationAlert()
  resetCommissioningRequest()

  try {
    await commissioningRequest({
      totalPowerKw: props.submission.systemPower,
      monitorings: [{
        brand: props.submission.brand,
        expectedPlantName: props.submission.plantName,
        inverters: props.submission.inverters.map(inv => inv.serialNumber),
      }],
    })

    holdUntilStatusChanges('WAITING_PARTNER_SUBMISSION')
    handleStepChange(steps.POLLING)
  }
  catch (err) {
    const errorCode = err instanceof CommissioningRequestError
      ? err.code
      : getErrorMessage(err)

    const status = resolveInstallationErrorStatus(errorCode)
    showInstallationAlert(status, { platformName: portalName.value })
    const alertConfig = getErrorAlertConfig(status, { platformName: portalName.value })

    if (alertConfig) {
      confirmationError.value = {
        title: alertConfig.title,
        text: alertConfig.text,
      }
    }
    else {
      confirmationError.value = {
        title: 'Não foi possível confirmar',
        text: getErrorMessage(err),
      }
    }
  }
}
</script>

<template>
  <div class="inverter-data-confirmation">
    <div class="confirmation-content">
      <h1 class="page-title">
        {{ t('inversorInstallation.form.title') }}
      </h1>

      <SolDivider thickness="x-small" orientation="horizontal" />

      <SolAlert
        v-if="confirmationError"
        id="confirmation-mutation-error"
        feedback="error"
        :title="confirmationError.title"
        class="mb-4"
        @close="confirmationError = null"
      >
        <div>
          <template v-if="Array.isArray(confirmationError.text)">
            <template v-for="(line, index) in confirmationError.text" :key="index">
              <span>{{ line }}</span>
              <br v-if="index < confirmationError.text.length - 1">
            </template>
          </template>
          <span v-else>{{ confirmationError.text }}</span>
        </div>
      </SolAlert>

      <div class="flex flex-col gap-5 w-full mt-2xs">
        <h2 class="text-xxs text-neutral-low-dark font-bold">
          {{ t('inversorInstallation.InversorInstallationConfirmation.title') }}
        </h2>

        <div class="flex flex-row flex-wrap gap-5 w-full justify-center items-center">
          <InversorCard
            v-for="(inverter, index) in submission.inverters"
            :key="index"
            :show-index="submission.inverters.length > 1"
            :inverter-number="index + 1"
            :brand="submission.brand"
            :serial="inverter.serialNumber"
            :plant-name="submission.plantName"
          />
        </div>
      </div>

      <SolDivider thickness="x-small" orientation="horizontal" class="m-2xs" />

      <div class="flex flex-row gap-2 justify-end w-full">
        <Tooltip
          v-if="hasPendingTAF"
          :text="t('inversorInstallation.form.taf_pending_tooltip')"
          position="top"
          width="300px"
          class="order-1 md:site:order-2"
        >
          <SolButton
            id="confirm-installation-data"
            variant="primary"
            size="medium"
            class="button"
            :loading="isLoading"
            :disabled="isButtonDisabled"
            @click="handleConfirm"
          >
            <template #icon-left>
              <CheckCircleIcon class="icon-size-medium" />
            </template>
            {{ t('inversorInstallation.InversorInstallationConfirmation.confirmButton') }}
          </SolButton>
        </Tooltip>
        <SolButton
          v-else
          id="confirm-installation-data"
          variant="primary"
          size="medium"
          class="button order-1 md:site:order-2"
          :loading="isLoading"
          :disabled="isButtonDisabled"
          @click="handleConfirm"
        >
          <template #icon-left>
            <CheckCircleIcon class="icon-size-medium" />
          </template>
          {{ t('inversorInstallation.InversorInstallationConfirmation.confirmButton') }}
        </SolButton>

        <SolButton
          id="edit-installation-data"
          variant="tertiary"
          size="medium"
          class="button order-2 md:site:order-1"
          :disabled="isButtonDisabled"
          @click="handleEdit"
        >
          <template #icon-left>
            <EditIcon class="icon-size-medium" />
          </template>
          {{ t('inversorInstallation.InversorInstallationConfirmation.reviewButton') }}
        </SolButton>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.inverter-data-confirmation {
  @apply flex flex-col justify-center items-center gap-2;
  @apply w-full py-2xs px-4xs rounded-sm;
  @apply bg-neutral-high-pure;

  @screen md:site {
    @apply items-start;
    @apply py-xs px-2xs rounded-lg;
  }

  .page-title {
    @apply font-highlight text-sm text-neutral-low-dark;
  }

  .confirmation-content {
    @apply w-full;
  }
}
</style>
